import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Package, Users, ShoppingCart, FileText, Filter, X, ChevronDown, ArrowLeft } from "lucide-react";
import { BottomNav } from "@/components/BottomNav";
import {
  useProducts,
  useVendors,
  usePurchases,
  useDeleteProduct,
  useDeleteVendor,
  useDeletePurchase,
  useLedger,
} from "@/hooks/useInventory";
import { useClients } from "@/hooks/useClients";
import { ProductCard } from "@/components/inventory/ProductCard";
import { VendorCard } from "@/components/inventory/VendorCard";
import { PurchaseCard } from "@/components/inventory/PurchaseCard";
import { LedgerCard } from "@/components/inventory/LedgerCard";
import { ProductFormModal } from "@/components/inventory/ProductFormModal";
import { VendorFormModal } from "@/components/inventory/VendorFormModal";
import { PurchaseFormModal } from "@/components/inventory/PurchaseFormModal";
import type {
  ProductWithStock,
  VendorWithOutstanding,
  InventoryPurchaseWithDetails,
  LedgerFilters,
} from "@/types/inventory";

type TabType = "products" | "vendors" | "purchases" | "ledger";

const VALID_TABS: TabType[] = ["products", "vendors", "purchases", "ledger"];

export function Inventory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab from URL or default to "products"
  const tabParam = searchParams.get("tab") as TabType | null;
  const activeTab: TabType = tabParam && VALID_TABS.includes(tabParam) ? tabParam : "products";

  const setActiveTab = (tab: TabType) => {
    setSearchParams({ tab });
  };
  const [showProductModal, setShowProductModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Edit state
  const [editingProduct, setEditingProduct] = useState<ProductWithStock | null>(null);
  const [editingVendor, setEditingVendor] = useState<VendorWithOutstanding | null>(null);
  const [editingPurchase, setEditingPurchase] = useState<InventoryPurchaseWithDetails | null>(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'product' | 'vendor' | 'purchase';
    item: any;
  } | null>(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const { data: productsData, isLoading: productsLoading } = useProducts({ is_active: true });
  const { data: vendorsData, isLoading: vendorsLoading } = useVendors({ is_active: true });
  const { data: purchasesData, isLoading: purchasesLoading } = usePurchases();

  const deleteProduct = useDeleteProduct();
  const deleteVendor = useDeleteVendor();
  const deletePurchase = useDeletePurchase();

  const products = productsData?.products || [];
  const vendors = vendorsData?.vendors || [];
  const purchases = purchasesData?.purchases || [];

  // Edit handlers
  const handleEditProduct = (product: ProductWithStock) => {
    setEditingProduct(product);
    setShowProductModal(true);
  };

  const handleEditVendor = (vendor: VendorWithOutstanding) => {
    setEditingVendor(vendor);
    setShowVendorModal(true);
  };

  const handleEditPurchase = (purchase: InventoryPurchaseWithDetails) => {
    setEditingPurchase(purchase);
    setShowPurchaseModal(true);
  };

  // Delete handlers
  const handleDeleteProduct = (product: ProductWithStock) => {
    setDeleteConfirm({ type: 'product', item: product });
  };

  const handleDeleteVendor = (vendor: VendorWithOutstanding) => {
    setDeleteConfirm({ type: 'vendor', item: vendor });
  };

  const handleDeletePurchase = (purchase: InventoryPurchaseWithDetails) => {
    setDeleteConfirm({ type: 'purchase', item: purchase });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      if (deleteConfirm.type === 'product') {
        await deleteProduct.mutateAsync(deleteConfirm.item.id);
        setToast({ message: 'Product deleted successfully', type: 'success' });
      } else if (deleteConfirm.type === 'vendor') {
        await deleteVendor.mutateAsync(deleteConfirm.item.id);
        setToast({ message: 'Vendor deleted successfully', type: 'success' });
      } else if (deleteConfirm.type === 'purchase') {
        await deletePurchase.mutateAsync(deleteConfirm.item.id);
        setToast({ message: 'Purchase deleted successfully', type: 'success' });
      }
      setDeleteConfirm(null);
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to delete', type: 'error' });
    }
  };

  // Calculate stats
  const totalProducts = products.length;
  const lowStockProducts = products.filter((p) => p.stock_status === "low" || p.stock_status === "out").length;
  const totalVendors = vendors.length;
  const outstandingAmount = vendors.reduce((sum, v) => sum + (v.outstanding_amount || 0), 0);

  const tabs = [
    { id: "products" as TabType, label: "Products", icon: Package },
    { id: "vendors" as TabType, label: "Vendors", icon: Users },
    { id: "purchases" as TabType, label: "Purchases", icon: ShoppingCart },
    { id: "ledger" as TabType, label: "Ledger", icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="p-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Inventory</h1>
            <p className="text-sm text-gray-500">
              Manage supplements, vendors, and stock
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="px-6 pb-4 grid grid-cols-2 gap-3">
          <StatCard
            label="Total Products"
            value={totalProducts}
            sublabel={lowStockProducts > 0 ? `${lowStockProducts} low stock` : "All stocked"}
            color={lowStockProducts > 0 ? "text-orange-600" : "text-green-600"}
          />
          <StatCard
            label="Active Vendors"
            value={totalVendors}
            sublabel={
              outstandingAmount > 0
                ? `₹${outstandingAmount.toFixed(0)} outstanding`
                : "All settled"
            }
            color={outstandingAmount > 0 ? "text-red-600" : "text-green-600"}
          />
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto scrollbar-hide border-b border-gray-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-[#5B8E87] text-[#5B8E87]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === "products" && (
          <ProductsTab
            products={products}
            isLoading={productsLoading}
            onAddProduct={() => setShowProductModal(true)}
            onEditProduct={handleEditProduct}
            onDeleteProduct={handleDeleteProduct}
          />
        )}
        {activeTab === "vendors" && (
          <VendorsTab
            vendors={vendors}
            isLoading={vendorsLoading}
            onAddVendor={() => setShowVendorModal(true)}
            onEditVendor={handleEditVendor}
            onDeleteVendor={handleDeleteVendor}
          />
        )}
        {activeTab === "purchases" && (
          <PurchasesTab
            purchases={purchases}
            isLoading={purchasesLoading}
            onAddPurchase={() => setShowPurchaseModal(true)}
            onEditPurchase={handleEditPurchase}
            onDeletePurchase={handleDeletePurchase}
          />
        )}
        {activeTab === "ledger" && <LedgerTab />}
      </div>

      {/* Floating Action Button */}
      <button
        onClick={() => {
          if (activeTab === "products") setShowProductModal(true);
          else if (activeTab === "vendors") setShowVendorModal(true);
          else if (activeTab === "purchases") setShowPurchaseModal(true);
          else alert("Feature coming soon!");
        }}
        className="fixed bottom-20 right-6 bg-[#5B8E87] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#4A7069] transition-colors z-30"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Product Form Modal */}
      {showProductModal && (
        <ProductFormModal
          isOpen={showProductModal}
          onClose={() => {
            setShowProductModal(false);
            setEditingProduct(null);
          }}
          onSuccess={(message) => {
            setToast({ message, type: 'success' });
            setShowProductModal(false);
            setEditingProduct(null);
          }}
          onError={(message) => {
            setToast({ message, type: 'error' });
          }}
          productId={editingProduct?.id}
          initialData={editingProduct || undefined}
        />
      )}

      {/* Vendor Form Modal */}
      {showVendorModal && (
        <VendorFormModal
          isOpen={showVendorModal}
          onClose={() => {
            setShowVendorModal(false);
            setEditingVendor(null);
          }}
          onSuccess={(message) => {
            setToast({ message, type: 'success' });
            setShowVendorModal(false);
            setEditingVendor(null);
          }}
          onError={(message) => {
            setToast({ message, type: 'error' });
          }}
          vendorId={editingVendor?.id}
          initialData={editingVendor || undefined}
        />
      )}

      {/* Purchase Form Modal */}
      {showPurchaseModal && (
        <PurchaseFormModal
          isOpen={showPurchaseModal}
          onClose={() => {
            setShowPurchaseModal(false);
            setEditingPurchase(null);
          }}
          onSuccess={(message) => {
            setToast({ message, type: 'success' });
            setShowPurchaseModal(false);
            setEditingPurchase(null);
          }}
          onError={(message) => {
            setToast({ message, type: 'error' });
          }}
          purchaseId={editingPurchase?.id}
          initialData={editingPurchase || undefined}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this {deleteConfirm.type}?{' '}
              {deleteConfirm.type === 'product' && `"${deleteConfirm.item.name}"`}
              {deleteConfirm.type === 'vendor' && `"${deleteConfirm.item.name}"`}
              {deleteConfirm.type === 'purchase' &&
                `purchase of ${deleteConfirm.item.product?.name || 'product'}`}
              ? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteProduct.isPending || deleteVendor.isPending || deletePurchase.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {(deleteProduct.isPending || deleteVendor.isPending || deletePurchase.isPending)
                  ? 'Deleting...'
                  : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
          <div
            className={`shadow-lg rounded-lg px-4 py-3 text-sm font-medium ${
              toast.type === 'error'
                ? 'bg-red-600 text-white'
                : 'bg-[#5B8E87] text-white'
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

function StatCard({
  label,
  value,
  sublabel,
  color,
}: {
  label: string;
  value: number;
  sublabel: string;
  color: string;
}) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className={`text-xs ${color} mt-1`}>{sublabel}</p>
    </div>
  );
}

function ProductsTab({
  products,
  isLoading,
  onAddProduct,
  onEditProduct,
  onDeleteProduct,
}: {
  products: ProductWithStock[];
  isLoading: boolean;
  onAddProduct: () => void;
  onEditProduct: (product: ProductWithStock) => void;
  onDeleteProduct: (product: ProductWithStock) => void;
}) {
  if (isLoading) {
    return <div className="flex justify-center py-12">
      <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#5B8E87] rounded-full" />
    </div>;
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
          <Package className="w-8 h-8 text-[#5B8E87]" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No products yet</h3>
        <p className="text-gray-500 text-center mb-6 max-w-sm">
          Add products to start managing your supplement inventory
        </p>
        <button
          onClick={onAddProduct}
          className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg hover:bg-[#4A7069] transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Product
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          onEdit={onEditProduct}
          onDelete={onDeleteProduct}
        />
      ))}
    </div>
  );
}

function VendorsTab({
  vendors,
  isLoading,
  onAddVendor,
  onEditVendor,
  onDeleteVendor,
}: {
  vendors: VendorWithOutstanding[];
  isLoading: boolean;
  onAddVendor: () => void;
  onEditVendor: (vendor: VendorWithOutstanding) => void;
  onDeleteVendor: (vendor: VendorWithOutstanding) => void;
}) {
  if (isLoading) {
    return <div className="flex justify-center py-12">
      <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#5B8E87] rounded-full" />
    </div>;
  }

  if (vendors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
          <Users className="w-8 h-8 text-[#5B8E87]" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No vendors yet</h3>
        <p className="text-gray-500 text-center mb-6 max-w-sm">
          Add vendors to track suppliers and manage purchases
        </p>
        <button
          onClick={onAddVendor}
          className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg hover:bg-[#4A7069] transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Vendor
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {vendors.map((vendor) => (
        <VendorCard
          key={vendor.id}
          vendor={vendor}
          onEdit={onEditVendor}
          onDelete={onDeleteVendor}
        />
      ))}
    </div>
  );
}

function PurchasesTab({
  purchases,
  isLoading,
  onAddPurchase,
  onEditPurchase,
  onDeletePurchase,
}: {
  purchases: InventoryPurchaseWithDetails[];
  isLoading: boolean;
  onAddPurchase: () => void;
  onEditPurchase: (purchase: InventoryPurchaseWithDetails) => void;
  onDeletePurchase: (purchase: InventoryPurchaseWithDetails) => void;
}) {
  if (isLoading) {
    return <div className="flex justify-center py-12">
      <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#5B8E87] rounded-full" />
    </div>;
  }

  if (purchases.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
          <ShoppingCart className="w-8 h-8 text-[#5B8E87]" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No purchases yet
        </h3>
        <p className="text-gray-500 text-center mb-6 max-w-sm">
          Record purchases to track inventory and vendor payments
        </p>
        <button
          onClick={onAddPurchase}
          className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg hover:bg-[#4A7069] transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Record Purchase
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {purchases.map((purchase) => (
        <PurchaseCard
          key={purchase.id}
          purchase={purchase}
          onEdit={onEditPurchase}
          onDelete={onDeletePurchase}
        />
      ))}
    </div>
  );
}

function LedgerTab() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<LedgerFilters>({
    transaction_type: "all",
  });
  const [showFilters, setShowFilters] = useState(false);

  const { data: ledgerData, isLoading } = useLedger(filters, page);
  const { data: productsData } = useProducts({ is_active: true });
  const { data: vendorsData } = useVendors({ is_active: true });
  const { data: clientsData } = useClients();

  const products = productsData?.products || [];
  const vendors = vendorsData?.vendors || [];
  const clients = clientsData?.clients || [];
  const transactions = ledgerData?.transactions || [];
  const totalCount = ledgerData?.total_count || 0;
  const summary = ledgerData?.summary;

  const hasActiveFilters =
    filters.product_id ||
    filters.vendor_id ||
    filters.client_id ||
    filters.date_from ||
    filters.date_to ||
    (filters.transaction_type && filters.transaction_type !== "all");

  const clearFilters = () => {
    setFilters({ transaction_type: "all" });
    setPage(1);
  };

  const handleFilterChange = (newFilters: Partial<LedgerFilters>) => {
    setFilters({ ...filters, ...newFilters });
    setPage(1);
  };

  if (isLoading && page === 1) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#5B8E87] rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Toggle Button */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors w-full justify-between"
      >
        <span className="flex items-center gap-2">
          <Filter className="w-4 h-4" />
          Filters
          {hasActiveFilters && (
            <span className="bg-[#5B8E87] text-white text-xs px-2 py-0.5 rounded-full">
              Active
            </span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            showFilters ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Filter Controls */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          {/* Transaction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction Type
            </label>
            <div className="flex gap-2">
              {[
                { value: "all", label: "All" },
                { value: "purchase", label: "IN" },
                { value: "assignment", label: "OUT" },
              ].map((type) => (
                <button
                  key={type.value}
                  onClick={() =>
                    handleFilterChange({
                      transaction_type: type.value as any,
                    })
                  }
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.transaction_type === type.value
                      ? "bg-[#5B8E87] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Product Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product
            </label>
            <select
              value={filters.product_id || ""}
              onChange={(e) =>
                handleFilterChange({
                  product_id: e.target.value || undefined,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
            >
              <option value="">All Products</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>

          {/* Vendor Filter - Show only if type is 'all' or 'purchase' */}
          {(filters.transaction_type === "all" ||
            filters.transaction_type === "purchase") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vendor
              </label>
              <select
                value={filters.vendor_id || ""}
                onChange={(e) =>
                  handleFilterChange({
                    vendor_id: e.target.value || undefined,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              >
                <option value="">All Vendors</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Client Filter - Show only if type is 'all' or 'assignment' */}
          {(filters.transaction_type === "all" ||
            filters.transaction_type === "assignment") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client
              </label>
              <select
                value={filters.client_id || ""}
                onChange={(e) =>
                  handleFilterChange({
                    client_id: e.target.value || undefined,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              >
                <option value="">All Clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Date
              </label>
              <input
                type="date"
                value={filters.date_from || ""}
                onChange={(e) =>
                  handleFilterChange({
                    date_from: e.target.value || undefined,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Date
              </label>
              <input
                type="date"
                value={filters.date_to || ""}
                onChange={(e) =>
                  handleFilterChange({
                    date_to: e.target.value || undefined,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Summary Stats - Show only when single product is selected */}
      {summary && filters.product_id && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Current Balance</p>
            <p className="text-2xl font-bold text-gray-900">
              {summary.current_balance}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {summary.total_in} IN • {summary.total_out} OUT
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Total Profit</p>
            <p
              className={`text-2xl font-bold ${
                summary.total_profit >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              ₹{summary.total_profit.toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 mt-1">From sales</p>
          </div>
        </div>
      )}

      {/* Transactions List */}
      {transactions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-[#5B8E87]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No transactions found
          </h3>
          <p className="text-gray-500 text-center max-w-sm">
            {hasActiveFilters
              ? "Try adjusting your filters"
              : "Record purchases and assignments to see them here"}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <LedgerCard
                key={transaction.id}
                transaction={transaction}
                showBalance={!!filters.product_id}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalCount > page * 10 && (
            <button
              onClick={() => setPage(page + 1)}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isLoading ? "Loading..." : "Load More"}
            </button>
          )}
        </>
      )}
    </div>
  );
}
